import { useRef, useState } from "react";

export type Status = "loading" | "ready" | "error";

type UpdateHandler = (abortController: AbortController) => Promise<void> | void;

export type StatusController = ReturnType<typeof useStatusController>;

type StatusListener = {
  statuses?: Status[];
  listener: (status: Status) => void;
};

export const useStatusController = () => {
  const abortControllerRef = useRef(new AbortController());
  const listeners = useRef<StatusListener[]>([]);
  const [status, _setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<Nullable<string>>(null);

  const setStatus = (newStatus: Status) => {
    _setStatus(newStatus);

    for (const l of listeners.current) {
      if (l.statuses && !l.statuses.includes(newStatus)) {
        continue;
      }

      l.listener(newStatus);
    }
  };

  const registerListener = (listener: StatusListener["listener"], statuses?: Status | Status[]) => {
    if (statuses && !Array.isArray(statuses)) {
      statuses = [statuses];
    }

    listeners.current.push({
      statuses: statuses,
      listener
    });

    return () => {
      listeners.current = listeners.current.filter(l => l.listener !== listener);
    };
  };

  const performUpdate = async (handleUpdate: UpdateHandler, message?: string) => {
    const abortController = new AbortController();

    try {
      abortControllerRef.current.abort();
      abortControllerRef.current = abortController;

      setStatus("loading");
      setMessage(message ?? null);
      await handleUpdate(abortController);

      if (abortController.signal.aborted) {
        return;
      }

      setStatus("ready");
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      setStatus("error");
    } finally {
      setMessage(null);
    }
  };

  return {
    registerListener,
    isLoading: status === "loading",
    isReady: status === "ready",
    hasError: status === "error",
    message,
    performUpdate,
    setLoading: () => setStatus("loading"),
    setReady: () => setStatus("ready"),
    setError: () => setStatus("error")
  };
};
